import { IconDownload, IconFilterOff, IconRefresh, IconSearch } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import { useSession } from '../auth/useSession'
import { Alert, Badge, EmptyState, ErrorState, FilteredEmptyState, LoadingState } from '../components/States'
import { ApiError } from '../lib/apiClient'
import { PAYMENT_STATUSES, type PaymentSummary } from '../lib/apiTypes'
import {
  formatCount,
  formatDateTimeWib,
  formatRupiah,
  inclusiveDayCount,
} from '../lib/format'
import { exportPayments, useClients, useMerchants, usePayments } from '../lib/hooks'
import {
  countActivePaymentFilters,
  emptyPaymentFilters,
  parsePaymentFilters,
  paymentQueryFromFilters,
  serializePaymentFilters,
  togglePaymentStatus,
  type PaymentFilters,
} from '../lib/paymentFilters'
import { paymentStatusLabel, paymentStatusTone } from '../lib/status'

const EXPORT_MAX_DAYS = 93

/**
 * Daftar transaksi untuk operator: filter yang tersimpan di query string,
 * kolom yang dipakai untuk memutuskan tindakan lanjutan, muat berikutnya dengan
 * cursor, dan ekspor CSV pada rentang tanggal yang sama.
 */
export function TransactionsPage() {
  const { user } = useSession()
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => parsePaymentFilters(params), [params])
  const [draft, setDraft] = useState<PaymentFilters>(filters)
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [extra, setExtra] = useState<PaymentSummary[]>([])
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<unknown>(null)
  const [exportDone, setExportDone] = useState('')

  // Query string adalah sumber kebenaran filter. Saat alamatnya berubah (lewat
  // tombol kembali atau tautan yang dibagikan), formulir dan penomoran halaman
  // disetel ulang sekali di dalam render, bukan lewat effect.
  const paramsKey = params.toString()
  const [syncedKey, setSyncedKey] = useState(paramsKey)
  if (paramsKey !== syncedKey) {
    setSyncedKey(paramsKey)
    setDraft(filters)
    setCursor(undefined)
    setExtra([])
  }

  const baseQuery = useMemo(() => paymentQueryFromFilters(filters), [filters])

  const query = usePayments({ ...baseQuery, cursor })
  const merchants = useMerchants()
  const clients = useClients()

  const rows = cursor ? mergeRows(extra, query.data?.data ?? []) : (query.data?.data ?? [])
  const activeFilters = countActivePaymentFilters(filters)

  function submitFilters(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setParams(serializePaymentFilters(draft), { replace: true })
  }

  function clearFilters() {
    setDraft(emptyPaymentFilters)
    setParams(new URLSearchParams(), { replace: true })
  }

  function loadMore() {
    if (query.data?.next_cursor) {
      setExtra(rows)
      setCursor(query.data.next_cursor)
    }
  }

  async function runExport() {
    setExportError(null)
    setExportDone('')
    setExporting(true)
    try {
      const result = await exportPayments({
        from: filters.from,
        to: filters.to,
        merchant: filters.merchant || undefined,
        status: filters.status.length > 0 ? filters.status.join(',') : undefined,
      })
      triggerDownload(result.blob, result.filename)
      setExportDone(result.filename)
    } catch (error) {
      setExportError(error)
    } finally {
      setExporting(false)
    }
  }

  const exportReady =
    Boolean(filters.from) &&
    Boolean(filters.to) &&
    inclusiveDayCount(filters.from, filters.to) <= EXPORT_MAX_DAYS &&
    inclusiveDayCount(filters.from, filters.to) > 0

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Transaksi</h1>
          <p className="muted">
            Cari transaksi berdasarkan status, merchant, client, rentang tanggal, atau
            nomor referensi. Filter yang aktif tersimpan di alamat halaman, sehingga
            tautan bisa dibagikan apa adanya.
          </p>
        </div>
      </div>

      {merchants.isError ? <ErrorState title="Gagal memuat pilihan merchant" error={merchants.error} onRetry={() => void merchants.refetch()} /> : null}
      {!user?.merchant_scope_restricted && clients.isError ? <ErrorState title="Gagal memuat pilihan client" error={clients.error} onRetry={() => void clients.refetch()} /> : null}
      <form className="card" onSubmit={submitFilters} aria-label="Filter transaksi">
        <div className="chip-row" role="group" aria-label="Filter status">
          {PAYMENT_STATUSES.map((status) => {
            const pressed = draft.status.includes(status)
            return (
              <button
                key={status}
                type="button"
                className="chip"
                aria-pressed={pressed}
                onClick={() => setDraft((current) => togglePaymentStatus(current, status))}
              >
                {paymentStatusLabel(status)}
              </button>
            )
          })}
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="field__label" htmlFor="f-merchant">
              Merchant
            </label>
            <select
              id="f-merchant"
              disabled={merchants.isPending || merchants.isError}
              className="select"
              value={draft.merchant}
              onChange={(event) =>
                setDraft((current) => ({ ...current, merchant: event.target.value }))
              }
            >
              <option value="">{merchants.isPending ? 'Memuat merchant...' : 'Semua merchant yang diizinkan'}</option>
              {(merchants.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name} ({item.slug})
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="f-client">
              Client
            </label>
            <select
              id="f-client"
              disabled={Boolean(user?.merchant_scope_restricted) || clients.isPending || clients.isError}
              className="select"
              value={draft.client}
              onChange={(event) =>
                setDraft((current) => ({ ...current, client: event.target.value }))
              }
            >
              <option value="">{user?.merchant_scope_restricted ? 'Client dalam cakupan merchant' : clients.isPending ? 'Memuat client...' : 'Semua client'}</option>
              {(clients.data?.data ?? []).map((item) => (
                <option key={item.id} value={item.slug}>
                  {item.name} ({item.slug})
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="f-from">
              Dari tanggal
            </label>
            <input
              id="f-from"
              className="input"
              type="date"
              value={draft.from}
              onChange={(event) =>
                setDraft((current) => ({ ...current, from: event.target.value }))
              }
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="f-to">
              Sampai tanggal
            </label>
            <input
              id="f-to"
              className="input"
              type="date"
              value={draft.to}
              onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="f-client-ref">
              Referensi client
            </label>
            <input
              id="f-client-ref"
              className="input"
              type="search"
              value={draft.clientReference}
              onChange={(event) =>
                setDraft((current) => ({ ...current, clientReference: event.target.value }))
              }
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="f-reference">
              Referensi bank
            </label>
            <input
              id="f-reference"
              className="input"
              type="search"
              value={draft.reference}
              onChange={(event) =>
                setDraft((current) => ({ ...current, reference: event.target.value }))
              }
            />
            <span className="field__hint">
              Cocok persis dengan nomor referensi partner, nomor referensi provider, atau
              RRN.
            </span>
          </div>

          <div className="field form-grid--full">
            <label className="checkbox" htmlFor="f-review">
              <input
                id="f-review"
                type="checkbox"
                checked={draft.needsReview}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, needsReview: event.target.checked }))
                }
              />
              Hanya yang perlu tinjauan
            </label>
          </div>
        </div>

        <div className="toolbar">
          <div className="toolbar__actions">
            <button type="submit" className="btn btn--primary">
              <IconSearch size={18} stroke={1.75} aria-hidden="true" />
              Terapkan filter
            </button>
            <button
              type="button"
              className="btn"
              onClick={clearFilters}
              disabled={activeFilters === 0 && !draft.needsReview}
            >
              <IconFilterOff size={18} stroke={1.75} aria-hidden="true" />
              Bersihkan
            </button>
          </div>
        </div>
      </form>

      <div className="card">
        <div className="card__head">
          <h2 className="card__title">Ekspor CSV</h2>
          <span className="card__hint">
            {activeFilters > 0 ? `${activeFilters} filter aktif ikut diekspor` : 'Tanpa filter'}
          </span>
        </div>
        <p className="muted">
          Berkas berisi kolom yang cukup untuk dicocokkan dengan mutasi rekening:
          identitas transaksi, waktu, merchant, client, referensi, status, nominal, dan
          penanda tinjauan. Rentang tanggal wajib dan paling lama {EXPORT_MAX_DAYS} hari.
        </p>
        <div className="state__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void runExport()}
            disabled={!exportReady || exporting}
          >
            <IconDownload size={18} stroke={1.75} aria-hidden="true" />
            {exporting ? 'Menyiapkan berkas...' : 'Ekspor CSV'}
          </button>
        </div>
        {!exportReady ? (
          <p className="field__hint">
            Isi rentang tanggal yang valid, dan pastikan rentangnya tidak lebih dari{' '}
            {EXPORT_MAX_DAYS} hari.
          </p>
        ) : null}
        {exportDone ? (
          <Alert tone="success">
            Berkas {exportDone} sudah diunduh. Periksa folder unduhan peramban.
          </Alert>
        ) : null}
        {exportError ? (
          <Alert tone="danger">
            {exportError instanceof ApiError
              ? `${exportError.message} (kode HTTP ${exportError.status}${
                  exportError.code ? `, ${exportError.code}` : ''
                })`
              : 'Ekspor gagal karena penyebab yang tidak diketahui.'}
          </Alert>
        ) : null}
      </div>

      {query.isPending ? (
        <LoadingState what="daftar transaksi" />
      ) : query.isError ? (
        <ErrorState
          title="Gagal memuat daftar transaksi"
          error={query.error}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          hint="Periksa filter yang dipakai: cursor yang rusak ditolak dengan kode 400."
        />
      ) : rows.length === 0 ? (
        activeFilters > 0 ? (
          <FilteredEmptyState what="transaksi" onClear={clearFilters} />
        ) : (
          <EmptyState
            title="Belum ada transaksi pada rentang ini"
            body="Server belum mencatat transaksi apa pun. Transaksi muncul setelah client membuat payment lewat Client API, atau setelah simulator sandbox dijalankan dari halaman detail."
          />
        )
      ) : (
        <section aria-labelledby="rows-heading">
          <div className="page-head">
            <div className="page-head__text">
              <h2 id="rows-heading">
                {formatCount(rows.length)} transaksi ditampilkan
              </h2>
              <p className="muted">Terbaru di atas, diurutkan dari waktu pembuatan.</p>
            </div>
          </div>

          <div className="table-scroll" tabIndex={0} role="region" aria-label="Tabel transaksi, geser horizontal dengan tombol panah">
            <table className="table">
              <caption className="visually-hidden">
                Daftar transaksi dengan status, merchant, client, referensi, nominal, dan
                waktu
              </caption>
              <thead>
                <tr>
                  <th scope="col">Waktu</th>
                  <th scope="col">Status</th>
                  <th scope="col">Merchant</th>
                  <th scope="col">Client</th>
                  <th scope="col">Referensi</th>
                  <th scope="col" className="is-num">
                    Nominal
                  </th>
                  <th scope="col">Tindakan</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="num">{formatDateTimeWib(row.created_at)}</span>
                      <br />
                      <span className="caption">Percobaan ke-{row.attempt_no}</span>
                    </td>
                    <td>
                      <Badge tone={paymentStatusTone(row.status)}>
                        {paymentStatusLabel(row.status)}
                      </Badge>
                      {row.needs_review ? (
                        <>
                          <br />
                          <Badge tone="review">Perlu tinjauan</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>{row.merchant}</td>
                    <td>{row.client}</td>
                    <td>
                      <span className="mono">{row.client_reference || 'tanpa referensi'}</span>
                      <br />
                      <span className="caption mono">
                        {row.rrn || row.provider_reference_no || 'belum ada referensi bank'}
                      </span>
                    </td>
                    <td className="is-num">
                      {formatRupiah(row.amount_minor)}
                      {row.paid_amount_minor !== null &&
                      row.paid_amount_minor !== row.amount_minor ? (
                        <>
                          <br />
                          <span className="caption">
                            dibayar {formatRupiah(row.paid_amount_minor)}
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td className="is-actions">
                      <Link className="table__link" to={`/transactions/${row.id}`}>
                        Buka detail
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="state__actions">
            {query.data?.next_cursor ? (
              <button
                type="button"
                className="btn"
                onClick={loadMore}
                disabled={query.isFetching}
              >
                <IconRefresh size={18} stroke={1.75} aria-hidden="true" />
                {query.isFetching ? 'Memuat...' : 'Muat berikutnya'}
              </button>
            ) : (
              <p className="muted">Semua baris yang cocok sudah ditampilkan.</p>
            )}
          </div>
        </section>
      )}
    </>
  )
}

function mergeRows(previous: PaymentSummary[], incoming: PaymentSummary[]): PaymentSummary[] {
  const seen = new Set(previous.map((row) => row.id))
  return [...previous, ...incoming.filter((row) => !seen.has(row.id))]
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
